import React, { useState } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import type { Filter } from 'app-components/flux/filters/filtersTypes';
import type { PlannedSpendingItem } from 'app-components/flux/free-to-spend/freeToSpendTypes';
import { tracker } from 'app-components/utils/core';

import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { actionHelpers } from 'app-components/flux/core';
import { filtersActions, filtersSelectors } from 'app-components/flux/filters';
import { freeToSpendActions, freeToSpendSelectors } from 'app-components/flux/free-to-spend';
import type { FreeToSpend } from 'app-components/flux/free-to-spend/freeToSpendTypes';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { numberUtils } from 'app-components/utils/number';
import { editPlannedSpendingSeriesDialog } from 'components/Dialogs/EditPlannedSpendingSeries/actions';
import StdDialog from 'components/Dialogs/StdDialog';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import CategoryField from 'components/QuickenControls/CategoryField';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import usePlannedCategoryChecker from 'hooks/usePlannedCategoryChecker';
import type { CoaType } from 'hooks/usePlannedCategoryChecker';
import { makeStyles } from 'tss-react/mui';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import { DateTime } from 'luxon';

const useStyles = makeStyles()((theme) => ({
  iconCat: {
    height: 40,
    width: 0,
    visibility: 'hidden',
    '&.visible': {
      paddingRight: 12,
      width: 40,
      visibility: 'visible',
    },
  },
  textSecondaryColor: {
    color: theme.palette.text.secondary,
  },
  catAndAmt: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  helperText: {
    maxWidth: '250px',
    width: '250px',
    display: 'flex',
  },
  spacer: {
    width: 24,
  },
  amountField: {
    margin: 0,
    textAlign: 'left',
  },
  dialogContent: {
    paddingTop: '16px !important',
  },
  suggested: {
    padding: theme.spacing(1),
    borderRadius: 12,
    width: '100%',
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0,
    marginBottom: theme.spacing(3),
  },
  show: {
    opacity: '1 !important',
  },
  submitButton: {
    marginLeft: 8,
  },
}));

const defaultPlanned = Object.freeze({ name: '', targetAmount: 0, recurring: true }) as PlannedSpendingItem;

function EditPlannedSpendingInstance({
  cardIndex,
  freeToSpendId,
  startDate,
  filterId,
  spendingThisMonth,
  location,
}: {
  cardIndex: number;
  freeToSpendId: string;
  startDate: string;
  filterId: string;
  spendingThisMonth: number;
  location: string;
}) {
  const [suggestedCategories, setSuggestedCategories] = usePreferenceV2('suggestedPSICategoryKnownIds');

  const plannedItems = useAppSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemsByF2SId(state, freeToSpendId),
  );
  const filter = useAppSelector((state) => filtersSelectors.getFilter(state, filterId));
  const freeToSpend = useAppSelector((state) => freeToSpendSelectors.getFreeToSpendById(state, freeToSpendId));
  const leftToSpend = useAppSelector((state) => freeToSpendSelectors.getLeftToSpend(state, freeToSpend as FreeToSpend));
  const calculatedPlannedAmount = useAppSelector((state) =>
    freeToSpendSelectors.getCalculatedPlannedSpending(state, freeToSpendId),
  );
  const parentCoaId = useAppSelector((state) => categoriesSelectors.getParentFromCoaList(state, filter));

  const plannedItem: PlannedSpendingItem = plannedItems?.get(cardIndex) || defaultPlanned;
  const { name } = plannedItem;
  const realTargetAmount = plannedItem.overwrittenTargetAmount;
  const [targetAmountState, setTarget] = useState(
    plannedItem.overwrittenTargetAmount == null ? plannedItem.targetAmount : plannedItem.overwrittenTargetAmount,
  );

  // eslint-disable-next-line no-unused-vars
  const [_validate, coa, coaSetter, errorText, currCategory, category, _isAlreadyTracked, children] =
    usePlannedCategoryChecker({
      id: parentCoaId,
      startDate,
      isEdit: true,
    });

  const isDisabled = realTargetAmount === targetAmountState && name === name && coa?.id === category?.id;

  const isDisabledOrError = isDisabled || !!errorText || targetAmountState <= 0;
  const { classes, cx } = useStyles();

  const dispatch = useAppDispatch();

  const removeCategoryFromSuggestedList = () => {
    if (suggestedCategories && setSuggestedCategories && suggestedCategories?.includes(currCategory?.knownCategoryId)) {
      setSuggestedCategories(suggestedCategories?.filter((item: string) => item !== currCategory?.knownCategoryId));
    }
  };

  const updateF2S = () => {
    const changed = [];
    if ((targetAmountState !== 0 && !targetAmountState) || targetAmountState < 0 || !coa.id) {
      return;
    }
    const item = plannedItems.get(cardIndex);
    if (!item) {
      return;
    }
    if (name !== item.name) {
      changed.push('name');
      item.name = name;
    }
    if (coa?.id !== category?.id) {
      changed.push('category');
      dispatch(
        filtersActions.updateFilter({
          id: filterId,
          filterItems: [
            {
              type: 'CHARTOFACCOUNT',
              chartOfAccounts: [
                (coa as any)?.toJS?.() || coa, // TODO: Remove 'any' usage
                ...(children?.map(({ id }) => ({ id, type: 'CATEGORY' })) ?? []),
              ],
            },
          ],
        } as Filter),
      );
    }

    if (targetAmountState !== realTargetAmount) {
      changed.push('amount');
      item.overwrittenTargetAmount = targetAmountState;
    }

    const dateObj = DateTime.fromISO(startDate);

    tracker.track(tracker.events.budgetItemEdit, {
      month: dateObj.month,
      year: dateObj.year,
      full_date: startDate,
      location,
      fields: changed,
    });

    const spendingItems = plannedItems.splice(cardIndex, 1, item); // TODO: change this to rely on first finding the item

    let total = 0;
    spendingItems?.forEach((plannedValue) => {
      const calculatedSpentAmount = plannedValue?.calculatedSpentAmount ?? 0;
      const overwrittenTargetAmount = plannedValue?.overwrittenTargetAmount ?? 0;
      const targetAmount = plannedValue?.targetAmount ?? 0;
      const spentAmount = -1 * calculatedSpentAmount;

      if ((overwrittenTargetAmount === 0 || overwrittenTargetAmount) && overwrittenTargetAmount >= spentAmount) {
        total += overwrittenTargetAmount;
      } else if (targetAmount > spentAmount) {
        total += targetAmount;
      } else {
        total += spentAmount;
      }
    });
    const leftToSpendAmount = leftToSpend - calculatedPlannedAmount - total;

    // We update the selected item first!
    actionHelpers
      .bindPromiseAction(
        dispatch,
        freeToSpendActions.updateFreeToSpend,
      )({
        id: freeToSpendId,
        plannedSpendingItems: spendingItems,
        calculatedPlannedSpendingAmount: -1 * total,
        leftToSpendAmount,
      })
      .then((_resp) => {
        removeCategoryFromSuggestedList();
      })
      .catch((error) => assert(false, error));
  };

  const onHandleClose = (event: any, reason: string) => {
    if (typeof event?.stopPropagation === 'function') {
      event?.stopPropagation();
    }
    if (typeof event?.preventDefault === 'function') {
      event?.preventDefault();
    }
    if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
      // update
      updateF2S();
    }
    dispatch(rootUiActions.removeDialog());

    // editHide();
  };

  const filterFn = (c: CoaType) =>
    c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE ||
    (c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isExpenseCat(null, c?.id ?? '')) ||
    c.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED; // only expense categories or uncat

  const formatAmount = useFormatAmount();
  const formatNumber = (value: number) =>
    formatAmount(Math.abs(value || 0), 'USD', '0,0.00', numberUtils.ShowSignEnum.NEGATIVE_ONLY);

  return (
    <StdDialog
      onClose={onHandleClose}
      onKeyDown={(e: any) => {
        if (e.code === 'Enter' && !isDisabledOrError) {
          onHandleClose(e, 'ENTER_KEY_DOWN');
        }
      }}
      open
      showCloseButton
      size={'sm'}
      title={"Edit this month's expense"}
    >
      <DialogContent className={classes.dialogContent}>
        <Box display={'box'}>
          <Box padding={2} paddingTop={2} maxWidth={'100%'}>
            <Stack spacing={3} width={'100%'}>
              <Typography variant='bodyRegular' component={'span'}>
                Changes will apply to this month only.
              </Typography>
              <div className={classes.catAndAmt}>
                <Box flexDirection='row' display='flex' justifyContent='flex-start' width={288} marginLeft={1}>
                  <div>
                    <Typography variant='captionRegular' className={cx(classes.helperText, classes.textSecondaryColor)}>
                      Expense Nickname
                    </Typography>
                    <Typography variant='bodyRegular' className={classes.helperText}>
                      {name}
                    </Typography>
                  </div>
                  <Box flexDirection={'column'} width={'100%'}>
                    <Typography variant='captionRegular' className={cx(classes.helperText, classes.textSecondaryColor)}>
                      Expense Category
                    </Typography>
                    <CategoryField
                      variant='bodyRegular'
                      id='monthly'
                      editable={false}
                      textFieldVariant='filled'
                      filterFn={filterFn}
                      value={coa}
                      onChange={(c: CoaType) => coaSetter(c, plannedItem)}
                      width={'250px'}
                      margin='none'
                      error={!!errorText}
                      helperText={errorText}
                      label='Select category'
                      textFieldProps={{
                        label: 'Category',
                        variant: 'filled',
                        margin: 'none',
                      }}
                    />
                  </Box>
                </Box>
                {<div className={classes.spacer} />}
              </div>
              <div className={classes.catAndAmt}>
                <Box flexDirection={'column'}>
                  <QAmountField
                    editable
                    id='target-amount'
                    variant={'filled'}
                    label={'Target amount this month'}
                    value={targetAmountState}
                    onChange={(e: any) => setTarget(Number(e.target.value))}
                    textFieldVariant={'filled'}
                    className={classes.amountField}
                    error={targetAmountState < 0}
                    expectedSign='positive'
                    margin='none'
                  />
                </Box>
              </div>
              <Box flexDirection={'column'} display={'flex'} justifyContent={'space-between'} width={'100%'}>
                <div className={cx(classes.suggested, classes.show)}>
                  <img
                    alt='cat-icon'
                    src={currCategory?.logoUrl || ''}
                    className={cx(classes.iconCat, currCategory?.logoUrl ? 'visible' : '')}
                  />
                  <Typography variant='paragraphRegular'>
                    {`${formatNumber(spendingThisMonth)} spent in this category so far this month.`}
                  </Typography>
                </div>
              </Box>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display={'flex'} justifyContent={'space-between'} width={'100%'}>
          <QButton
            id={'edit-planned-series-button'}
            onClick={(e: any) => {
              onHandleClose(e, 'EXIT');
              dispatch(editPlannedSpendingSeriesDialog(freeToSpendId, cardIndex, startDate, filterId, location));
            }}
          >
            Edit expense series
          </QButton>
          <Box>
            <QButton
              variant='contained'
              type='submit'
              id={'update-button'}
              onClick={(e: any) => {
                onHandleClose(e, 'SUBMIT_BUTTON');
              }}
              disabled={isDisabledOrError}
              className={classes.submitButton}
            >
              UPDATE
            </QButton>
          </Box>
        </Box>
      </DialogActions>
    </StdDialog>
  );
}

export default React.memo(EditPlannedSpendingInstance);
